// The tunable rule numbers live in rules/config.js (DEFAULT_RULES); the
// constants below that name them are aliases of those defaults, kept for the
// app, which always plays the defaults. Rules code reads rules() instead, so a
// simulation can run another variant.
import { DEFAULT_RULES, rules } from './config.js'

export const ARMIES_PER_ROUND = DEFAULT_RULES.armiesPerRound
export const BATTLEFIELD_COUNT = 7
export const BATTLEFIELD_CAPACITIES = [5, 4, 3, 2, 3, 4, 5]
export const DISPLAY_DEPTH = 5

// Generates random corridor capacities (capacityMin–capacityMax per column,
// 2–5 by default) for a new War. `rng` returns a float in [0, 1) — Math.random
// in the app, a seeded generator in a simulation — so the rules module itself
// never reaches for global randomness.
export function generateWarCapacities(rng) {
    const { capacityMin, capacityMax } = rules()
    return Array.from({ length: BATTLEFIELD_COUNT }, () => Math.floor(rng() * (capacityMax - capacityMin + 1)) + capacityMin)
}
export const ARMY_ICON_COUNT_DESKTOP = 9
export const ARMY_ICON_COUNT_MOBILE = 4
// A Base is a BROOD: its two funding drones stand on the outer cells of its
// 3-cell bar and fight from there, so a mature brood is worth 2 — one per
// live drone (subplotBases.broodStrength). The centre cell is the nest: the
// egg that becomes each round's airdrop. (Internal fort* names kept, as with
// "Base" before it.) (TUNE)
export const FORT_STRENGTH = DEFAULT_RULES.fortStrength
export const FORT_IN_CONSTRUCTION_STRENGTH = DEFAULT_RULES.fortInConstructionStrength
// Rounds of attacks a Base under construction must survive before it matures
// (at the start of the following planning phase). (TUNE)
export const FORT_MATURATION_ROUNDS = DEFAULT_RULES.fortMaturationRounds
// Resupply drop zones granted per mature Base per round. Player-facing name for
// a fort is "Base"; internal fort* field names are kept to avoid migrating
// serialized board state. (TUNE)
export const DROP_ZONES_PER_BASE = DEFAULT_RULES.dropZonesPerBase

// ── Inter-round missile animation timing ──────────────────────────────────────
// Single source of truth shared by HexBoard (which runs the animation) and
// useGameState (which sequences each clash's phases around missile arrival).
//
// The clash plays as a SEQUENTIAL DUEL, not simultaneous volleys: the attacker
// fires one shot, the defender answers a beat later and intercepts it at the
// divider, then the next exchange begins — back and forth. A fort fires one of
// its shots per exchange until spent. Once one side runs out, the other's
// remaining (surplus) shots fly on to their target one at a time.
export const MISSILE_RISE_MS           = 880   // attacker shot's slow climb from its row to the meeting point
export const MISSILE_RESPONSE_MS       = 620   // defender waits this long, then darts out to intercept
export const MISSILE_MEET_OFFSET       = 3     // collision sits this far (top%) past the divider, into the defender's half
export const MISSILE_EXCHANGE_GAP_MS   = 260   // pause after a collision before the next exchange fires
export const MISSILE_SURPLUS_RISE_MS   = 1050  // a surplus shot's slow flight onward to its target plot (eases off as it comes down)
// Gap between consecutive surplus shots. Sets the staged-crater cadence too —
// each landing shot swaps in its ring of the footprint ladder at impact, and a
// ring needs ~1s to read (fire-sweep delay to its outer cells + the 500ms fade
// + 750ms ignition flash), so at 400ms the rings blurred together. 700ms gives
// each strike its own beat while staying snappier than the 1140ms duel cadence.
export const MISSILE_SURPLUS_STAGGER_MS = 700
export const ROCKET_TRAVEL_MS          = 560   // defender rocket flight to its interception point

// One full back-and-forth exchange (attacker fires → shots collide → brief pause).
export const MISSILE_EXCHANGE_MS = MISSILE_RISE_MS + MISSILE_EXCHANGE_GAP_MS

// Time (ms) from clash start until the winner's first surplus shot strikes the
// target plot. Surplus fire begins only once the dueling exchanges are done, so
// the start point is the same regardless of which side won.
export function missileImpactMs(pairCount, absorbed = 0) {
    return pairCount * MISSILE_EXCHANGE_MS
        + absorbed * MISSILE_SURPLUS_STAGGER_MS + MISSILE_SURPLUS_RISE_MS
}

// Generous upper bound (ms) for when the LAST shot of a clash has resolved —
// used to hold the clash open long enough that no missile is unmounted mid-air.
// Without a target the winner's leftover shots stay on the slower dueling
// cadence (MissileLayer's !isExcess branch) instead of the surplus stagger.
export function missileFlightTotalMs(selfStrength, oppStrength, hasTarget = true) {
    const numSelf = selfStrength ?? 0, numOpp = oppStrength ?? 0
    const pairCount = Math.min(numSelf, numOpp)
    const surplus   = Math.max(numSelf, numOpp) - pairCount
    const pairsEnd   = pairCount > 0 ? (pairCount - 1) * MISSILE_EXCHANGE_MS + MISSILE_RISE_MS : 0
    const surplusEnd = surplus > 0
        ? (hasTarget
            ? pairCount * MISSILE_EXCHANGE_MS + (surplus - 1) * MISSILE_SURPLUS_STAGGER_MS + MISSILE_SURPLUS_RISE_MS
            : (pairCount + surplus - 1) * MISSILE_EXCHANGE_MS + MISSILE_RISE_MS)
        : 0
    return Math.max(pairsEnd, surplusEnd)
}
// ── Opponent round replay (post-REVEAL, pre-clash) ────────────────────────────
// Before the first clash spotlights, the opponent's half briefly replays what
// they did this round — movements first (one cross-corridor hop per beat, via
// the same FLIP slide the planning board uses), then new Base builds, then all
// fresh deploys gliding in from their rear edge in one staggered beat. Shared
// by gameLogic (which builds the step boards and their hold times), useGameState
// (which sequences the steps) and HexBoard (which animates the entrances).
export const REPLAY_LEAD_MS           = 350  // beat after the frost lifts before the first motion
export const REPLAY_MOVE_MS           = 720  // hold per cross-corridor move beat (480ms hop + read time)
// Hold per new-Base build beat: covers the funding drones' send-off (wiggle +
// descent, below) plus read time on the construction that appears.
export const REPLAY_BUILD_MS          = 1400
// The funding drones' send-off during the opponent replay's build beat: a
// brief in-place lift-off, then the descent from their corridor plots down
// to the Base's footprint cells — shrinking steadily the whole way, the
// ground shadow closing under each tile as it lands (launchBuildMote).
export const BASE_BUILD_WIGGLE_MS     = 250
export const BASE_BUILD_FLIGHT_MS     = 950
export const REPLAY_DEPLOY_GLIDE_MS   = 520  // one fresh deploy's glide-in from the rear edge
export const REPLAY_DEPLOY_STAGGER_MS = 220  // gap between consecutive deploy glide-ins
// A fresh deploy stands on the ground until its player submits; then it
// lifts off as a veteran — the tile grows to flying size as its altitude
// shadow appears beneath it (useTokenFlip). Your own lift plays at SUBMIT;
// the opponent's plays on their replay's settle beat, which holds long
// enough for it to finish before the first clash.
export const DEPLOY_LIFT_MS           = 560
export const REPLAY_SETTLE_MS         = DEPLOY_LIFT_MS + 200  // final settle on the submitted board before the clash

export const MESSAGE_TIMEOUT_MS = 5000
export const MESSAGE_FADE_MS = 500

// Yield economy
// Yield cost to buy one rocket defense. (TUNE — see yieldPacing.test.js for
// the current first-rocket affordability crossing.)
export const ROCKET_COST = DEFAULT_RULES.rocketCost

// Yield cost of one surveillance drone. Launched from a live structure cell the
// round it's bought, it reveals a 5-subplot cardinal cross on the opponent's
// side of the SAME corridor (permanent reveal, distinct from crater damage) and
// stamps nearest-structure intel at the target cell. Single-use, unarmed,
// uninterceptable. The defender never learns the launch cell — only that a
// drone came down that corridor. (TUNE)
export const DRONE_COST = DEFAULT_RULES.droneCost
// Reveal-phase drone animation: flight to the target, hover while the scan
// sweeps in, then fade. The homefront swap (scan appearing) fires at
// DRONE_FLIGHT_MS; HexBoard's flight keyframes stay in sync with all three.
export const DRONE_FLIGHT_MS = 1700
export const DRONE_HOVER_MS  = 1000

// Yield (rules v8): lots produce it, extractors multiply it (see
// subplotEconomy.tickYield). Structures produce nothing themselves.
export const YIELD_PER_LOT_PER_ROUND = DEFAULT_RULES.yieldPerLotPerRound
export const EXTRACTOR_YIELD_MULTIPLIER = DEFAULT_RULES.extractorYieldMultiplier

// ── Homefront v3 — subplot block system (crater damage + structure kills) ─────
// Crater damage is a fixed symmetric hex footprint centred on the aim cell,
// growing with margin: 7 / 13 / 19 / 25 / 31 / 37 cells, capped at 43 for 7+.
// The shape ladder lives in subplotDamage.js (craterFootprintOffsets).
// Scoring is structure kills ONLY: a structure is destroyed when every one of
// its subplots is a crater (partial coverage leaves it damaged and standing),
// and the first player to lose STRUCTURES_TO_WIN structures loses the war.
// Cells destroyed count for nothing on their own, and Bases don't count. Land
// destruction keeps its full economic and intel effect. There is no round
// limit; both players losing their last structure in the same round is a draw.
// Both Grids must field at least this many structures at war start
// (structureKills.assertWarGrid) — the threshold is never clamped down.
export const STRUCTURES_TO_WIN = DEFAULT_RULES.structuresToWin
// Structure intel: a resolved crater stamps the Chebyshev distance (in subplots)
// to the nearest surviving enemy structure within this range. Infinity = unlimited
// (beyond range shows nothing).
export const BEARING_RANGE = DEFAULT_RULES.bearingRange ?? Infinity
// Rocket protective coverage: Chebyshev ring around the host structure footprint.
export const ROCKET_COVERAGE_RADIUS = 3
// Homefront maker: how many buildings a custom homefront must field — a flat
// 7, no range (min = max). Of the QA presets only Contiguous (7) is lockable;
// Mosaic (6) and Structure-dense (10) remain damage fixtures. (TUNE)
export const HOMEFRONT_MIN_STRUCTURES = DEFAULT_RULES.homefrontMinStructures
export const HOMEFRONT_MAX_STRUCTURES = DEFAULT_RULES.homefrontMaxStructures
// No stockpiling one footprint: at most this many buildings of any one shape
// (single / tall / wide / diagonal / anti-diag). (TUNE)
export const HOMEFRONT_MAX_PER_SHAPE  = DEFAULT_RULES.homefrontMaxPerShape
